import { useMemo } from "react";
import {
    checkUserPermissionFunc,
    patchMasterBlockFloorPermissions,
} from "../components/common/CommonFunctions";
import {
    normalizePermKey,
    passesRoleGate,
    readSessionRoles,
} from "../config/navPermissions";

function hasSubMenuPermission(module, permKey, flag = "permission") {
    const perm = checkUserPermissionFunc(module, permKey, "SubMenu");
    if (!perm?.permission) return false;
    if (!flag || flag === "permission" || flag === "view") return true;
    return !!perm?.[flag];
}

function hasMenuPermission(module) {
    const perm = checkUserPermissionFunc(module, null, "Menu");
    return !!perm?.permission;
}

function passesFeatureFlag(flag) {
    if (!flag) return true;
    const customConfig = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
    if (flag === "nabh") return !!customConfig.is_nabh;
    return true;
}

function passesEmedhubRequirement(requireEmedhub) {
    if (!requireEmedhub) return true;
    return JSON.parse(sessionStorage.getItem("emedhubConnected") || "false");
}

/**
 * Central permission check shared by sidebar filtering and route guards.
 * @param {object} spec rule from navPermissions (module, permKey, selfService, roleGate, …)
 */
export function evaluatePermissionAccess(spec, roles = readSessionRoles()) {
    if (!spec) return true;
    if (spec.open) {
        if (spec.requireEmedhub && !passesEmedhubRequirement(true)) return false;
        if (spec.requireAdmin && !roles.isAdmin && !roles.isSystemAdmin) return false;
        return true;
    }

    if (spec.requireAdmin) {
        return roles.isAdmin || roles.isSystemAdmin;
    }

    if (!passesFeatureFlag(spec.featureFlag)) return false;
    if (!passesEmedhubRequirement(spec.requireEmedhub)) return false;
    if (!passesRoleGate(spec.roleGate, roles)) return false;

    if (spec.orgAdminOnly && !roles.isOrgAdmin) return false;

    if (roles.isAdmin || roles.isSystemAdmin) return true;

    const module = spec.module;
    const permKey = spec.permKey;

    if (spec.menuLevel) {
        return hasMenuPermission(module);
    }

    if (spec.permKeys?.length) {
        const moduleName = spec.module;
        const any = spec.permKeys.some((k) => hasSubMenuPermission(moduleName, k));
        if (any) return true;
    }

    if (permKey) {
        const flags = spec.flags?.length ? spec.flags : (spec.flag ? [spec.flag] : ["permission"]);
        if (flags.some((flag) => hasSubMenuPermission(module, permKey, flag))) return true;
    }

    return false;
}

/** Whether a top-level menu label is in the fullMenus utility list for this user. */
export function isFullMenuLabel(label, defaults) {
    return defaults?.fullMenus?.includes(normalizePermKey(label)) ?? false;
}

export function usePermissionAccess(spec) {
    return useMemo(
        () => evaluatePermissionAccess(spec),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(spec)]
    );
}

export { readSessionRoles, hasSubMenuPermission, hasMenuPermission, patchMasterBlockFloorPermissions };
